import React, { useState, useEffect } from 'react';
import Modal from 'react-modal'; // npm i react-modal react-icons
import { PageURLMaster } from '../../APIs/APIUrls';
import './LocationSearch.css';

const LocationModal = ({ isOpen, onClose, onSelect, setLocationName }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAlphabet, setSelectedAlphabet] = useState('');
  const [currentLocation, setCurrentLocation] = useState('No location found'); // State for dynamic geolocation
  const [geoDenied, setGeoDenied] = useState(false);
  const [resolvingLocation, setResolvingLocation] = useState(false);
  const [pageUrlMasterData, setPageUrlMasterData] = useState([]);
  const [popularCities, setPopularCities] = useState([
    { name: 'Agra', icon: '🏛️' },
    { name: 'Bahadurgarh', icon: '🏰' },
    { name: 'Chandigarh', icon: '🏛️' },
    { name: 'Delhi', icon: '🏢' },
    { name: 'Gurgaon', icon: '🏢' },
    { name: 'Haridwar', icon: '⛩️' },
    { name: 'Jaipur', icon: '🏰' },
    { name: 'Rohtak', icon: '🏛️', selected: true },
  ]);

  const iconByCity = {
    Delhi: '🏢',
    Gurgaon: '🏢',
    Noida: '🏢',
    Chandigarh: '🏛️',
    Jaipur: '🏰',
    Agra: '🏛️',
    Haridwar: '⛩️',
    Rohtak: '🏛️',
    Mumbai: '🌆',
    Pune: '🌇',
    Bangalore: '🌃',
    Hyderabad: '🌆',
    Chennai: '🌊',
    Kolkata: '🌉',
  };

  const getCityIcon = (city) => iconByCity[city] || '📍';

  const toCityName = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item.trim();
    return (item.City || item.city || '').toString().trim();
  };

  const buildPopularCitiesFromPageUrls = (rows) => {
    const unique = new Set();
    const cities = [];

    (rows || []).forEach((row) => {
      const cityName = toCityName(row);
      if (!cityName) return;
      const key = cityName.toLowerCase();
      if (unique.has(key)) return;
      unique.add(key);
      cities.push({ name: cityName, icon: getCityIcon(cityName) });
    });

    if (!cities.length) {
      return [

        { name: 'Chandigarh', icon: '🏛️' },
        { name: 'Delhi', icon: '🏢' },
        { name: 'Gurgaon', icon: '🏢' },
        { name: 'Rohtak', icon: '🏛️', selected: true },
      ];
    }

    return cities.slice(0, 8).map((city) => ({
      ...city,
      selected: city.name.toLowerCase() === currentLocation.toLowerCase(),
    }));
  };

  // Fetch user's geolocation and get city name
  useEffect(() => {
    const getGeolocation = () => {

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {

            const { latitude, longitude } = position.coords;
            try {
              // Use BigDataCloud for reverse geocoding
              const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}`
              );
              const data = await response.json();

              // Extract the city name only from the response
              const city = (data?.city || 'No location found').trim() || 'No location found';


              onSelect(city);
              setCurrentLocation(city);
              setLocationName(city);
            } catch (error) {
              console.error('Reverse geocoding error:', error);
              setCurrentLocation('No location found');
            }
          },
          (error) => {
            console.error('Geolocation error:', error);
            // If permission denied, track it so we can surface the typed-location CTA
            if (error && error.code === 1) { // PERMISSION_DENIED
              setGeoDenied(true);
            }
            setCurrentLocation('No location found');
          }
        );
      }
    };

    getGeolocation();
  }, []);

  // Allow user to use typed searchQuery as current location. This will try to validate
  // via Nominatim (returns English names) and fall back to a cleaned title-case string.
  const handleUseTypedLocation = async () => {
    const q = String(searchQuery || '').trim();
    if (!q) return;

    setResolvingLocation(true);
    try {
      // Fast path: if it matches a known city from the list, use that exact casing
      const qLower = q.toLowerCase();
      const exact = allCities.find((c) => c.toLowerCase() === qLower);
      if (exact) {
        setCurrentLocation(exact);
        onSelect(exact);
        if (typeof setLocationName === 'function') setLocationName(exact);
        setSearchQuery('');
        setSelectedAlphabet('');
        setGeoDenied(false);
        return;
      }

      // Query Nominatim search endpoint to get a canonical English place name
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&accept-language=en&q=${encodeURIComponent(q)}`;
      const resp = await fetch(url);
      const json = await resp.json();
      if (Array.isArray(json) && json.length > 0) {
        const item = json[0];
        // prefer address fields, then display_name
        const name = (
          item?.address?.city || item?.address?.town || item?.address?.village || item?.address?.county || item?.display_name
        ).toString();
        // take first part of display_name if long
        const cleaned = name.split(',')[0].trim();
        setCurrentLocation(cleaned);
        onSelect(cleaned);
        if (typeof setLocationName === 'function') setLocationName(cleaned);
        setSearchQuery('');
        setSelectedAlphabet('');
        setGeoDenied(false);
        return;
      }

      // Final fallback: title-case the typed value and use it
      const fallback = q.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(' ');
      setCurrentLocation(fallback);
      onSelect(fallback);
      if (typeof setLocationName === 'function') setLocationName(fallback);
      setSearchQuery('');
      setSelectedAlphabet('');
      setGeoDenied(false);
    } catch (err) {
      console.error('Location lookup failed:', err);
      const fallback = q.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(' ');
      setCurrentLocation(fallback);
      onSelect(fallback);
      if (typeof setLocationName === 'function') setLocationName(fallback);
      setSearchQuery('');
      setSelectedAlphabet('');
      setGeoDenied(false);
    } finally {
      setResolvingLocation(false);
    }
  };

  // Fetch all page URLs from backend (POST) and populate popular cities using City from JSON
  useEffect(() => {
    const fetchAllPageUrlMasterForWeb = async () => {
      try {
        const response = await fetch(`${PageURLMaster}GetAllPageURLMasterforWeb`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const result = await response.json();
        const rows = result?.data || result?.Data || [];

        setPageUrlMasterData(rows);
        setPopularCities(buildPopularCitiesFromPageUrls(rows));
      } catch (error) {
        console.error('GetAllPageURLMasterforWeb error:', error);
      }
    };

    fetchAllPageUrlMasterForWeb();
  }, []);

  // Update selected popular city when geolocation city changes
  useEffect(() => {
    setPopularCities((prev) =>
      prev.map((city) => ({
        ...city,
        selected: city.name.toLowerCase() === currentLocation.toLowerCase(),
      }))
    );
  }, [currentLocation]);



  const allCities = [
    // Andhra Pradesh
    'Visakhapatnam', 'Vijayawada', 'Hyderabad', 'Warangal', 'Kurnool', 'Rajahmundry', 'Kakinada', 'Tenali', 'Kadapa', 'Tirupati', 'Chittoor', 'Guntur', 'Nellore', 'Hindupur',
    // Arunachal Pradesh
    'Itanagar', 'Naharlagun', 'Papum Pare', 'Pasighat', 'Tezu', 'Ziro',
    // Assam
    'Guwahati', 'Dibrugarh', 'Silchar', 'Nagaon', 'Tinsukia', 'Barpeta', 'Dhubri', 'Golaghat',
    // Bihar
    'Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga', 'Purnia', 'Hajipur', 'Munger', 'Saharsa', 'Jehanabad', 'Nalanda', 'Katihar', 'Arrah',
    // Chhattisgarh
    'Raipur', 'Bilaspur', 'Durg', 'Rajnandgaon', 'Jagdalpur', 'Raigarh', 'Mandir Hasaud', 'Dhamtari',
    // Goa
    'Panaji', 'Margao', 'Vasco da Gama', 'Pernem', 'Curchorem',
    // Gujarat
    'Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Jamnagar', 'Junagadh', 'Porbandar', 'Bhavnagar', 'Godhra', 'Gandhinagar', 'Anand', 'Morvi', 'Visnagar', 'Vapi', 'Mehsana', 'Himmatnagar', 'Surendranagar',
    // Haryana
    'Faridabad', 'Gurgaon', 'Hisar', 'Rohtak', 'Panipat', 'Ambala', 'Yamunanagar', 'Kurukshetra', 'Kaithal', 'Karnal', 'Sonipat', 'Palwal', 'Bahadurgarh', 'Bhiwani', 'Narwana',
    // Himachal Pradesh
    'Shimla', 'Solan', 'Kangra', 'Mandi', 'Hamirpur', 'Chamba', 'Kinnaur', 'Lahaul Spiti', 'Bilaspur', 'Rampur',
    // Jharkhand
    'Ranchi', 'Jamshedpur', 'Dhanbad', 'Giridih', 'Bokaro', 'Hazaribag', 'Koderma', 'Dumka', 'Deoghar', 'East Singhbhum', 'Pakur', 'Godda',
    // Karnataka
    'Bangalore', 'Mangalore', 'Hubballi', 'Belgaum', 'Gulbarga', 'Davanagere', 'Tumkur', 'Hassan', 'Chikmagalur', 'Shimoga', 'Bijapur', 'Kolar', 'Raichur', 'Udupi', 'Chitradurga', 'Ballari',
    // Kerala
    'Kochi', 'Thiruvananthapuram', 'Kozhikode', 'Kannur', 'Kottayam', 'Ernakulam', 'Pathanamthitta', 'Idukki', 'Wayanad', 'Alappuzha', 'Thrissur', 'Malappuram', 'Kasaragod',
    // Madhya Pradesh
    'Indore', 'Bhopal', 'Jabalpur', 'Gwalior', 'Ujjain', 'Sagar', 'Satna', 'Rewa', 'Khandwa', 'Khargone', 'Mandsaur', 'Dhar', 'Hoshangabad', 'Vidisha', 'Chhindwara', 'Singrauli',
    // Maharashtra
    'Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Akola', 'Amravati', 'Kolhapur', 'Sangli', 'Satara', 'Latur', 'Jalgaon', 'Dhule', 'Nanded', 'Ratnagiri', 'Sindhudurg', 'Ahmednagar', 'Yavatmal',
    // Manipur
    'Imphal', 'Bishnupur', 'Jiribam', 'Kakching', 'Tamenglong',
    // Meghalaya
    'Shillong', 'Tura', 'Jowai', 'Nongstoin',
    // Mizoram
    'Aizawl', 'Lunglei', 'Saiha', 'Kolasib',
    // Nagaland
    'Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto',
    // Odisha
    'Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Balasore', 'Sambalpur', 'Puri', 'Dhenkanal', 'Angul', 'Kalahandi', 'Bargarh', 'Sundargarh', 'Bhadrak',
    // Punjab
    'Chandigarh', 'Amritsar', 'Ludhiana', 'Jalandhar', 'Patiala', 'Bathinda', 'Firozpur', 'Hoshiarpur', 'Sangrur', 'Ferozepur', 'Mukhariya', 'Malerkotla',
    // Rajasthan
    'Jaipur', 'Jodhpur', 'Kota', 'Ajmer', 'Udaipur', 'Bikaner', 'Alwar', 'Nagaur', 'Pali', 'Sikar', 'Churu', 'Barmer', 'Jaisalmer', 'Hanumangarh', 'Ganganagar', 'Bhilwara', 'Dungarpur', 'Banswara',
    // Sikkim
    'Gangtok', 'Namchi', 'Lachung', 'Pelling', 'Ravangla',
    // Tamil Nadu
    'Chennai', 'Coimbatore', 'Madurai', 'Salem', 'Tiruppur', 'Erode', 'Tiruchirappalli', 'Vellore', 'Kanchipuram', 'Tirunelveli', 'Thanjavur', 'Villupuram', 'Dindigul', 'Thoothukudi', 'Cuddalore', 'Ranipet', 'Pollachi',
    // Telangana
    'Hyderabad', 'Secunderabad', 'Warangal', 'Khammam', 'Karimnagar', 'Ramagundam', 'NiZamabad', 'Medak', 'Adilabad', 'Mahbubnagar', 'Suryapet', 'Tandur',
    // Tripura
    'Agartala', 'Udaipur', 'Dharmanagar', 'Kailasahar', 'Ambassa',
    // Uttar Pradesh
    'Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Meerut', 'Allahabad', 'Bareilly', 'Aligarh', 'Ghaziabad', 'Noida', 'Mathura', 'Jhansi', 'Moradabad', 'Saharanpur', 'Azamgarh', 'Firozabad', 'Bulandshahr', 'Gorakhpur', 'Sahaspur', 'Etah', 'Etawah', 'Farrukhabad', 'Fatehpur', 'Gonda', 'Hapur', 'Hardoi', 'Hathras', 'Kannauj', 'Kaushambi', 'Kheri', 'Lalitpur', 'Mirzapur', 'Muzaffarnagar', 'Muzaffarpur', 'Orai', 'Rae Bareli', 'Rampur', 'Ranikhet', 'Ratlam', 'Raya', 'Raysing', 'Safipur', 'Sambhal', 'Sandi', 'Shahjahanpur', 'Shamli', 'Shravasti', 'Siddharthnag', 'Sitapur', 'Sultanpur', 'Tanda', 'Unnao', 'Uttar Pradesh',
    // Uttarakhand
    'Dehradun', 'Nainital', 'Haridwar', 'Roorkee', 'Almora', 'Bageshwar', 'Chamoli', 'Champawat', 'Garhwal', 'Pithoragarh', 'Rudraprayag', 'Sitarganj', 'Udham Singh Nagar', 'Uttarkashi',
    // West Bengal
    'Kolkata', 'Howrah', 'Darjeeling', 'Siliguri', 'Asansol', 'Durgapur', 'Jalpaiguri', 'Chandannagore', 'Serampore', 'Kharagpur', 'Arambagh', 'Balurghat', 'Bankura', 'Bardhaman', 'Birbhum', 'Cooch Behar', 'East Midnapore', 'Hooghly', 'Hugli', 'Malda', 'Medinipur', 'Nadia', 'North 24 Parganas', 'Purba Medinipur', 'Paschim Medinipur', 'Purulia', 'South 24 Parganas', 'Uttar Dinajpur', 'West Dinajpur',
    // Union Territories
    'Andaman and Nicobar Islands', 'Port Blair', 'Chandigarh', 'Dadra and Nagar Haveli', 'Silvassa', 'Daman and Diu', 'Daman', 'Diu', 'Delhi', 'New Delhi', 'Jammu', 'Srinagar', 'Leh', 'Ladakh', 'Puducherry', 'Yanam', 'Mahe', 'Karikal',
  ];

  const alphabets = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Filter cities by alphabet
  const filteredByAlphabet = selectedAlphabet
    ? allCities.filter(city => city[0].toUpperCase() === selectedAlphabet)
    : [];

  // Filter cities by search query
  const filteredCities = popularCities.filter(city =>
    city.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const trimmedQuery = searchQuery.trim();

  // Combined filtering: search suggestions and alphabet results
  const filteredSuggestions = trimmedQuery
    ? allCities
      .filter((city) => city.toLowerCase().includes(trimmedQuery.toLowerCase()))
      .slice(0, 12)
    : filteredByAlphabet;

  const showSuggestionList = trimmedQuery.length > 0 || selectedAlphabet.length > 0;

  const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const renderHighlightedText = (text, query) => {
    if (!query) {
      return text;
    }

    const parts = text.split(new RegExp(`(${escapeRegex(query)})`, 'ig'));
    return parts.map((part, index) => (
      part.toLowerCase() === query.toLowerCase()
        ? <span key={`${part}-${index}`} className="location-match-highlight">{part}</span>
        : <span key={`${part}-${index}`}>{part}</span>
    ));
  };

  const handleSelect = (city) => {
    const selectedLocationName = typeof city === 'string' ? city : city.name;
    onSelect(selectedLocationName);
    if (typeof setLocationName === 'function') {
      setLocationName(selectedLocationName);
    }
    setSearchQuery('');
    setSelectedAlphabet('');
    onClose();
  };

  const handleAlphabetClick = (letter) => {
    // Toggle alphabet selection: deselect if already selected, otherwise select it
    if (selectedAlphabet === letter) {
      setSelectedAlphabet('');
    } else {
      setSelectedAlphabet(letter);
    }
    setSearchQuery('');
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setSelectedAlphabet('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      closeTimeoutMS={500}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
      }}
      className={{
        base: 'location-modal-content',
        afterOpen: 'location-modal-content-open',
        beforeClose: 'location-modal-content-close',
      }}
      overlayClassName={{
        base: 'location-modal-overlay',
        afterOpen: 'location-modal-overlay-open',
        beforeClose: 'location-modal-overlay-close',
      }}
      ariaHideApp={false}
    >
      {/* Flex Container */}
      <div className="location-modal-theme" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #dbe3ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="location-modal-title" style={{ margin: 0 }}>Select Location</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        {/* Search Input */}
        <div style={{ padding: '16px 24px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search city, area or locality"
              value={searchQuery}
              onChange={handleSearchChange}
              className="location-modal-input"
              style={{
                flex: 1, padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '12px',
                background: '#ffffff', boxSizing: 'border-box',
              }}
            />
            <button
              type="button"
              onClick={handleUseTypedLocation}
              disabled={!searchQuery.trim() || resolvingLocation}
              title={resolvingLocation ? 'Resolving...' : 'Use typed location'}
              style={{
                padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', background: resolvingLocation ? '#f1f5f9' : '#eff6ff',
                color: '#0f172a', cursor: (!searchQuery.trim() || resolvingLocation) ? 'not-allowed' : 'pointer',
                fontWeight: 600,
              }}
            >
              {resolvingLocation ? 'Using...' : 'Use typed location'}
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="location-modal-scrollable" style={{ flex: 1, overflowY: 'auto', paddingLeft: '24px', paddingRight: '24px' }}>
          {showSuggestionList ? (
            /* Suggestion List View */
            <>
              {filteredSuggestions.length > 0 ? (
                <div className="location-suggestion-list">
                  {filteredSuggestions.map((location) => (
                    <button
                      key={location}
                      type="button"
                      className="location-suggestion-item"
                      onClick={() => handleSelect(location)}
                    >
                      {trimmedQuery ? renderHighlightedText(location, trimmedQuery) : location}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="location-no-results">No matching cities found</div>
              )}
            </>
          ) : (
            /* Default View: Current Location + Popular Cities */
            <>
              {/* Current Location */}
              {
                currentLocation !== 'No location found' && (
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      onClick={() => handleSelect(currentLocation)}
                      style={{
                        width: '100%', padding: '12px 16px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '12px',
                        display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                      }}
                    >
                      <span style={{ color: '#2563eb', fontSize: '14px' }}>📍</span>
                      <span style={{ color: '#1e3a8a', fontWeight: 500 }}>Use current location</span>
                      <span style={{ marginLeft: 'auto', color: '#1d4ed8', fontWeight: 600, fontSize: '16px' }}>{currentLocation}</span>
                    </button>
                  </div>
                )
              }


              {/* Popular Cities Grid */}
              <div>
                <h3 className="location-modal-subtitle" style={{ margin: '0 0 16px' }}>Popular Cities</h3>
                <div className="location-popular-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                  {filteredCities.map((city) => (
                    <button
                      key={city.name}
                      className="location-popular-city-btn"
                      onClick={() => handleSelect(city)}
                      style={{
                        padding: '16px 12px', border: city.selected ? '2px solid #2563eb' : '1px solid #dbe3ef',
                        borderRadius: '12px', background: '#ffffff', cursor: 'pointer', textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                      }}
                    >
                      <div style={{ fontSize: '28px', marginBottom: '4px' }}>{city.icon}</div>
                      <div className="location-popular-city-name" style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{city.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Alphabet Filter - ALWAYS at Bottom */}
        {/* <div style={{ padding: '16px 24px', borderTop: '1px solid #dbe3ef', background: '#ffffff' }}>
          <h3 className="location-modal-subtitle" style={{ margin: '0 0 12px', fontSize: '14px' }}>All Cities</h3>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {alphabets.map(letter => (
              <button
                key={letter}
                className="location-modal-chip"
                style={{
                  padding: '8px 12px',
                  color: selectedAlphabet === letter ? '#ffffff' : '#64748b',
                  background: selectedAlphabet === letter ? '#2563eb' : '#f8fafc',
                  border: selectedAlphabet === letter ? 'none' : '1px solid #cbd5e1',
                  borderRadius: '6px', cursor: 'pointer',
                  whiteSpace: 'nowrap', minWidth: '36px',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleAlphabetClick(letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        </div> */}
      </div>
    </Modal>
  );
};

export default LocationModal;

// // Usage in parent
// const App = () => {
//   const [modalOpen, setModalOpen] = useState(false);
//   const [selectedLocation, setSelectedLocation] = useState('');

//   return (
//     <div>
//       <input type="text" value={selectedLocation} onClick={() => setModalOpen(true)} readOnly placeholder="Search location" />
//       <LocationModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSelect={setSelectedLocation} />
//     </div>
//   );
// };
